import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/ui/Navbar";
import PageSizeSelect from "../components/ui/PageSizeSelect";
import CronogramaForm from "../components/ui/CronogramaForm";
import CronogramaMasivoForm from "../components/ui/CronogramaMasivoForm";
import AsignarForm from "../components/ui/AsignarForm";
import AlarmaModal from "../components/ui/AlarmaModal";
import AsignarSegundoFirmanteModal from "../components/calibracion/AsignarSegundoFirmanteModal";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Headset,
  LoaderPinwheel,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  CalendarCheck2,
  CalendarClock,
  Bell,
  UserCheck,
  UserRoundCheck,
} from "lucide-react";
import { verReporteIndividual } from "../utils/reports";
import { hasPermission } from "../utils/permissions";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const TiposMantenimiento = ["Preventivo", "Correctivo", "Calibración"];
const Estados = [
  "Pendiente",
  "Programado",
  "Borrador",
  "Completado",
  "Cerrado",
  "Cancelado",
];
const ESTADOS_GESTIONADOS = new Set(["Completado", "Cerrado", "Cancelado"]);
const CHART_COLORS = ["#2563eb", "#16a34a", "#f97316", "#dc2626"];

const ESTADO_BADGE = {
  Pendiente: "bg-amber-50 text-amber-700 border border-amber-200",
  Programado: "bg-blue-50 text-blue-700 border border-blue-200",
  Borrador: "bg-violet-50 text-violet-700 border border-violet-200",
  Completado: "bg-green-50 text-green-700 border border-green-200",
  Cerrado: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Cancelado: "bg-red-50 text-red-700 border border-red-200",
};

const TIPO_BADGE = {
  Preventivo: "bg-blue-50 text-blue-700 border border-blue-200",
  Correctivo: "bg-orange-50 text-orange-700 border border-orange-200",
  Calibración: "bg-violet-50 text-violet-700 border border-violet-200",
};

const ServiciosPage = () => {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingEquipos, setLoadingEquipos] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingSedes, setLoadingSedes] = useState(false);

  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const [filterSede, setFilterSede] = useState("");
  const [filterSedeGrupo, setFilterSedeGrupo] = useState("");
  const [sedeGrupos, setSedeGrupos] = useState([]);

  const [showAsignar, setShowAsignar] = useState(false);
  const [showSegundoFirmante, setShowSegundoFirmante] = useState(false);
  // eslint-disable-next-line
  const [asignarData, setAsignarData] = useState(null);

  const [areas, setAreas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState("programados");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showFormMasive, setShowFormMasive] = useState(false);
  const [editingMasive, setEditingMasive] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [alarmas, setAlarmas] = useState([]);
  const [loadingAlarmas, setLoadingAlarmas] = useState(false);
  const [alarmaModal, setAlarmaModal] = useState(null);
  const [alarmaSearch, setAlarmaSearch] = useState("");
  const [alarmaPage, setAlarmaPage] = useState(1);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const location = useLocation();

  const handleAsignar = (servicio) => {
    setAsignarData(servicio);
    setShowAsignar(true);
  };

  const fetchServicios = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { sort: "-createdAt" };
      if (opts.search ?? search) params.search = opts.search ?? search;
      if (opts.tipo ?? filterTipo) params.tipo = opts.tipo ?? filterTipo;
      if (opts.estado ?? filterEstado)
        params.estado = opts.estado ?? filterEstado;
      if (opts.area ?? filterArea) params.area = opts.area ?? filterArea;
      const res = await axios.get(`${API}/cronograma`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setServicios(res.data);
      setPage(1);
    } catch (err) {
      toast.error("Error al cargar servicios");
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    setLoadingAreas(true);
    try {
      const res = await axios.get(`${API}/areas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAreas(res.data);
    } catch (err) {
      toast.error("Error al cargar áreas");
    } finally {
      setLoadingAreas(false);
    }
  };

  const fetchEquipos = async () => {
    setLoadingEquipos(true);
    try {
      const res = await axios.get(`${API}/equipos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEquipos(res.data || []);
    } catch (err) {
      toast.error("Error al cargar equipos para métricas");
    } finally {
      setLoadingEquipos(false);
    }
  };

  const fetchClientes = async () => {
    setLoadingClientes(true);
    try {
      const res = await axios.get(`${API}/clientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientes(res.data || []);
    } catch (err) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoadingClientes(false);
    }
  };

  const fetchSedesByCliente = async (clienteId) => {
    if (!clienteId) {
      setSedes([]);
      return;
    }
    setLoadingSedes(true);
    try {
      const res = await axios.get(`${API}/sedes/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSedes(res.data || []);
    } catch (err) {
      toast.error("Error al cargar sedes");
      setSedes([]);
    } finally {
      setLoadingSedes(false);
    }
  };

  const fetchAlarmas = async () => {
    setLoadingAlarmas(true);
    try {
      const res = await axios.get(`${API}/cronograma/alarmas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlarmas(res.data.alarmas || []);
    } catch (err) {
      toast.error("Error al cargar alarmas");
    } finally {
      setLoadingAlarmas(false);
    }
  };

  useEffect(() => {
    fetchServicios();
    fetchAreas();
    fetchEquipos();
    fetchClientes();
    axios
      .get(`${API}/sede-grupos`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setSedeGrupos(r.data || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (location.state?.tab) setActiveTab(location.state.tab);
  }, [location.state]);

  useEffect(() => {
    if (activeTab === "alarmas" && alarmas.length === 0) fetchAlarmas();
    // eslint-disable-next-line
  }, [activeTab]);

  const handleSearch = () => fetchServicios({ search });
  const handleFilterTipo = (value) => {
    setFilterTipo(value);
    fetchServicios({ tipo: value });
  };
  const handleFilterEstado = (value) => {
    setFilterEstado(value);
    fetchServicios({ estado: value });
  };
  const handleFilterArea = (value) => {
    setFilterArea(value);
    fetchServicios({ area: value });
  };
  const handleFilterCliente = async (value) => {
    setFilterCliente(value);
    setFilterSede("");
    await fetchSedesByCliente(value);
  };
  const handleFilterSede = (value) => {
    setFilterSede(value);
  };

  const handleClearFilters = async () => {
    setSearch("");
    setFilterTipo("");
    setFilterEstado("");
    setFilterArea("");
    setFilterCliente("");
    setFilterSede("");
    setFilterSedeGrupo("");
    setSedes([]);
    setSelectedYear(new Date().getFullYear());
    setActiveTab("programados");
    setPage(1);
    await fetchServicios({ search: "", tipo: "", estado: "", area: "" });
    await Promise.all([fetchAreas(), fetchEquipos(), fetchClientes()]);
  };

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const handleCreateMasive = () => {
    setEditingMasive(null);
    setShowFormMasive(true);
  };
  const handleEdit = (serv) => {
    setEditing(serv);
    setShowForm(true);
  };
  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este servicio?")) return;
    try {
      await axios.delete(`${API}/cronograma/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info("Servicio eliminado");
      fetchServicios();
    } catch (err) {
      toast.error("Error al eliminar servicio");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `¿Eliminar la programación de ${selectedIds.size} servicio(s) seleccionado(s)?`,
      )
    )
      return;
    setDeletingBulk(true);
    try {
      const res = await axios.delete(`${API}/cronograma/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: Array.from(selectedIds) },
      });
      toast.success(res.data.message || "Programaciones eliminadas");
      setSelectedIds(new Set());
      fetchServicios();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al eliminar programaciones",
      );
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (editing) {
        await axios.put(`${API}/cronograma/${editing._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Servicio actualizado");
      } else {
        await axios.post(`${API}/cronograma`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Servicio creado");
      }
      setShowForm(false);
      setEditing(null);
      fetchServicios();
    } catch (err) {
      toast.error("Error al guardar servicio");
    }
  };

  const grupoSedeIds = useMemo(() => {
    if (!filterSedeGrupo) return null;
    const g = sedeGrupos.find((g) => g._id === filterSedeGrupo);
    return g ? new Set(g.sedes.map((s) => s._id || s)) : null;
  }, [filterSedeGrupo, sedeGrupos]);

  const serviciosFiltradosClienteSede = useMemo(() => {
    return servicios.filter((s) => {
      const clienteId = s.equipo?.cliente?._id || s.equipo?.cliente;
      const sedeId = s.equipo?.sede?._id || s.equipo?.sede;
      if (grupoSedeIds) return grupoSedeIds.has(sedeId);
      const cumpleCliente = filterCliente ? clienteId === filterCliente : true;
      const cumpleSede = filterSede ? sedeId === filterSede : true;
      return cumpleCliente && cumpleSede;
    });
  }, [servicios, filterCliente, filterSede, grupoSedeIds]);

  const totalEquiposFiltrados = useMemo(() => {
    return equipos.filter((e) => {
      const clienteId = e.cliente?._id || e.cliente;
      const sedeId = e.sede?._id || e.sede;
      const cumpleCliente = filterCliente ? clienteId === filterCliente : true;
      const cumpleSede = filterSede ? sedeId === filterSede : true;
      return cumpleCliente && cumpleSede;
    }).length;
  }, [equipos, filterCliente, filterSede]);

  const yearOptions = useMemo(() => {
    const years = new Set([new Date().getFullYear()]);
    serviciosFiltradosClienteSede.forEach((s) => {
      if (s.fechaProgramada)
        years.add(new Date(s.fechaProgramada).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [serviciosFiltradosClienteSede]);

  const serviciosDelAnio = useMemo(
    () =>
      serviciosFiltradosClienteSede.filter(
        (s) =>
          s.fechaProgramada &&
          new Date(s.fechaProgramada).getFullYear() === Number(selectedYear),
      ),
    [serviciosFiltradosClienteSede, selectedYear],
  );

  const metricas = useMemo(() => {
    const equiposProgramados = new Set(
      serviciosDelAnio.map((s) => s.equipo?._id).filter(Boolean),
    );
    const equiposGestionados = new Set(
      serviciosDelAnio
        .filter((s) => ESTADOS_GESTIONADOS.has(s.estado))
        .map((s) => s.equipo?._id)
        .filter(Boolean),
    );
    const porcentajeProgramados = totalEquiposFiltrados
      ? (equiposProgramados.size / totalEquiposFiltrados) * 100
      : 0;
    const sinProgramar = Math.max(
      totalEquiposFiltrados - equiposProgramados.size,
      0,
    );
    const porcentajeGestionados = totalEquiposFiltrados
      ? (equiposGestionados.size / totalEquiposFiltrados) * 100
      : 0;
    return {
      equiposProgramados: equiposProgramados.size,
      porcentajeProgramados,
      sinProgramar,
      porcentajeGestionados,
      equiposGestionados: equiposGestionados.size,
    };
  }, [serviciosDelAnio, totalEquiposFiltrados]);

  const serviciosOrdenados = useMemo(() => {
    return [...serviciosFiltradosClienteSede].sort((a, b) => {
      const vencidoDiff =
        Number(Boolean(b.vencido)) - Number(Boolean(a.vencido));
      if (vencidoDiff !== 0) return vencidoDiff;
      return new Date(a.fechaProgramada) - new Date(b.fechaProgramada);
    });
  }, [serviciosFiltradosClienteSede]);

  const serviciosProgramados = useMemo(
    () => serviciosOrdenados.filter((s) => !ESTADOS_GESTIONADOS.has(s.estado)),
    [serviciosOrdenados],
  );
  const serviciosGestionados = useMemo(
    () => serviciosOrdenados.filter((s) => ESTADOS_GESTIONADOS.has(s.estado)),
    [serviciosOrdenados],
  );

  const serviciosVisibles =
    activeTab === "gestionados" ? serviciosGestionados : serviciosProgramados;

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return serviciosVisibles.slice(start, start + pageSize);
  }, [serviciosVisibles, page, pageSize]);

  const totalPages = Math.max(
    1,
    Math.ceil(serviciosVisibles.length / pageSize),
  );

  const selectableIds = useMemo(
    () =>
      paged
        .filter((s) => ["Programado", "Pendiente"].includes(s.estado))
        .map((s) => s._id),
    [paged],
  );

  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds));
    }
  };

  const alarmasFiltradas = useMemo(() => {
    if (!alarmaSearch.trim()) return alarmas;
    const q = alarmaSearch.toLowerCase();
    return alarmas.filter((a) => {
      const eq = a.equipo;
      return (
        eq?.serial?.toLowerCase().includes(q) ||
        eq?.marca?.name?.toLowerCase().includes(q) ||
        eq?.modelo?.name?.toLowerCase().includes(q) ||
        eq?.cliente?.name?.toLowerCase().includes(q) ||
        eq?.area?.name?.toLowerCase().includes(q)
      );
    });
  }, [alarmas, alarmaSearch]);

  const alarmasPaged = useMemo(() => {
    const start = (alarmaPage - 1) * pageSize;
    return alarmasFiltradas.slice(start, start + pageSize);
  }, [alarmasFiltradas, alarmaPage, pageSize]);

  const alarmasTotalPages = Math.max(
    1,
    Math.ceil(alarmasFiltradas.length / pageSize),
  );

  useEffect(() => {
    setAlarmaPage(1);
  }, [alarmaSearch]);
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [activeTab]);

  const pieProgramadosData = [
    { name: "Programados", value: metricas.equiposProgramados },
    {
      name: "Sin programar",
      value: Math.max(totalEquiposFiltrados - metricas.equiposProgramados, 0),
    },
  ];

  const isPageLoading =
    loading ||
    loadingAreas ||
    loadingEquipos ||
    loadingClientes ||
    loadingSedes;

  const barGestionData = [
    {
      name: `${selectedYear}`,
      gestionados: Number(metricas.porcentajeGestionados.toFixed(1)),
      objetivo: 100,
    },
  ];

  const handleVerReporte = async (id) => {
    setIsLoading(id);
    await verReporteIndividual(id);
    setIsLoading(null);
  };

  const hasActiveFilters =
    search ||
    filterTipo ||
    filterEstado ||
    filterArea ||
    filterCliente ||
    filterSede ||
    filterSedeGrupo;

  const TABS = [
    {
      key: "programados",
      label: "Programados",
      count: serviciosProgramados.length,
      icon: CalendarClock,
    },
    {
      key: "gestionados",
      label: "Gestionados",
      count: serviciosGestionados.length,
      icon: CalendarCheck2,
    },
    {
      key: "alarmas",
      label: "Alarmas",
      count: alarmas.length,
      icon: Bell,
      danger: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <Headset className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Servicios</h1>
              <p className="text-sm text-gray-500">
                Cronograma de mantenimientos y servicios
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasPermission("servicios.asignar") && (
              <button
                onClick={handleAsignar}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <UserCheck className="h-4 w-4" /> Asignar servicios
              </button>
            )}
            {hasPermission("servicios.segundaFirma") && (
              <button
                onClick={() => setShowSegundoFirmante(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-violet-300 rounded-lg text-violet-700 hover:bg-violet-50 transition-colors"
              >
                <UserRoundCheck className="h-4 w-4" /> Segundo firmante
              </button>
            )}
            {hasPermission("servicios.crear") && (
              <>
                <button
                  onClick={handleCreateMasive}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Agendar servicios
                </button>
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" /> Nuevo servicio
                </button>
              </>
            )}
          </div>
        </div>

        {isPageLoading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 items-end">
            {/* Buscar */}
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Código o cliente…"
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Tipo
              </label>
              <select
                value={filterTipo}
                onChange={(e) => handleFilterTipo(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                <option value="">Todos los tipos</option>
                {TiposMantenimiento.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </select>
            </div>

            {/* Estado */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Estado
              </label>
              <select
                value={filterEstado}
                onChange={(e) => handleFilterEstado(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                <option value="">Todos los estados</option>
                {Estados.map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </select>
            </div>

            {/* Área */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Área
              </label>
              <select
                value={filterArea}
                onChange={(e) => handleFilterArea(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                <option value="">Todas las áreas</option>
                {areas.map((area) => (
                  <option key={area._id} value={area._id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Cliente */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Cliente
              </label>
              <select
                value={filterCliente}
                onChange={(e) => {
                  setFilterSedeGrupo("");
                  handleFilterCliente(e.target.value);
                }}
                disabled={Boolean(filterSedeGrupo)}
                className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">Todos los clientes</option>
                {clientes.map((cliente) => (
                  <option key={cliente._id} value={cliente._id}>
                    {cliente.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sede */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Sede
              </label>
              <select
                value={filterSede}
                onChange={(e) => handleFilterSede(e.target.value)}
                disabled={!filterCliente || Boolean(filterSedeGrupo)}
                className="w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">Todas las sedes</option>
                {sedes.map((sede) => (
                  <option key={sede._id} value={sede._id}>
                    {sede.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Grupo sedes (conditional) + active filters */}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            {sedeGrupos.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-500">
                  Grupo de sedes:
                </label>
                <select
                  value={filterSedeGrupo}
                  onChange={(e) => {
                    setFilterSedeGrupo(e.target.value);
                    if (e.target.value) {
                      setFilterCliente("");
                      setFilterSede("");
                      setSedes([]);
                    }
                  }}
                  className="text-sm border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                >
                  <option value="">Sin grupo</option>
                  {sedeGrupos.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {hasActiveFilters && (
              <>
                <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full font-medium">
                  Filtros activos · {serviciosFiltradosClienteSede.length}{" "}
                  resultado
                  {serviciosFiltradosClienteSede.length !== 1 ? "s" : ""}
                </span>
                <button
                  onClick={handleClearFilters}
                  className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  <X className="h-3 w-3" /> Limpiar filtros
                </button>
              </>
            )}
          </div>
        </div>

        {/* Indicadores */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Indicadores de servicios
            </h3>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Año:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="text-sm border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-400 focus:outline-none"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-blue-600">
                Equipos con servicio
              </p>
              <p className="mt-1 text-3xl font-bold text-blue-800">
                {metricas.porcentajeProgramados.toFixed(1)}%
              </p>
              <p className="text-xs text-blue-500 mt-0.5">
                {metricas.equiposProgramados} de {totalEquiposFiltrados} equipos
              </p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-amber-600">
                Sin programar
              </p>
              <p className="mt-1 text-3xl font-bold text-amber-800">
                {metricas.sinProgramar}
              </p>
              <p className="text-xs text-amber-500 mt-0.5">
                equipos sin mantenimiento asignado
              </p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-600">
                % Gestionados
              </p>
              <p className="mt-1 text-3xl font-bold text-emerald-800">
                {metricas.porcentajeGestionados.toFixed(1)}%
              </p>
              <p className="text-xs text-emerald-500 mt-0.5">
                {metricas.equiposGestionados} equipos completados/cerrados
              </p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="h-56 rounded-lg border border-gray-100 bg-gray-50 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieProgramadosData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={84}
                    label
                  >
                    {pieProgramadosData.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="h-56 rounded-lg border border-gray-100 bg-gray-50 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barGestionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar
                    dataKey="gestionados"
                    fill="#16a34a"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1.5 mb-4 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {TABS.map(({ key, label, count, icon: Icon, danger }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                    active
                      ? danger
                        ? "bg-red-600 text-white shadow-sm"
                        : "bg-blue-600 text-white shadow-sm"
                      : danger
                        ? "text-red-600 hover:bg-red-50"
                        : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {count > 0 && (
                    <span
                      className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                        active
                          ? "bg-white/25"
                          : danger
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tabla Servicios */}
        {activeTab !== "alarmas" && (
          <>
            {activeTab === "programados" &&
              hasPermission("servicios.desprogramar") &&
              selectedIds.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg mb-3">
                  <span className="text-sm text-red-700 font-medium">
                    {selectedIds.size} seleccionado(s)
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    disabled={deletingBulk}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {deletingBulk
                      ? "Eliminando..."
                      : "Desprogramar seleccionados"}
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
              <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
                <PageSizeSelect
                  value={pageSize}
                  onChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                      {activeTab === "programados" &&
                        hasPermission("servicios.desprogramar") && (
                          <th className="py-3 px-4 w-10">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 cursor-pointer"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                              disabled={selectableIds.length === 0}
                            />
                          </th>
                        )}
                      <th className="py-3 px-4 text-left">Cliente</th>
                      <th className="py-3 px-4 text-left">Sede</th>
                      <th className="py-3 px-4 text-left">Equipo</th>
                      <th className="py-3 px-4 text-left">Área</th>
                      <th className="py-3 px-4 text-left">Tipo</th>
                      <th className="py-3 px-4 text-left">Fecha</th>
                      <th className="py-3 px-4 text-left">Estado</th>
                      <th className="py-3 px-4 text-left">Responsable</th>
                      <th className="py-3 px-4 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td
                          colSpan={
                            activeTab === "programados" &&
                            hasPermission("servicios.desprogramar")
                              ? 10
                              : 9
                          }
                          className="py-12 text-center"
                        >
                          <LoaderPinwheel className="h-6 w-6 animate-spin text-blue-400 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm">
                            Cargando servicios...
                          </p>
                        </td>
                      </tr>
                    ) : paged.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            activeTab === "programados" &&
                            hasPermission("servicios.desprogramar")
                              ? 10
                              : 9
                          }
                          className="py-16 text-center"
                        >
                          <Headset className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                          <p className="text-gray-500 font-medium">
                            No se encontraron servicios
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            Intenta ajustar los filtros
                          </p>
                        </td>
                      </tr>
                    ) : (
                      paged.map((s) => (
                        <tr
                          key={s._id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {activeTab === "programados" &&
                            hasPermission("servicios.desprogramar") && (
                              <td className="py-2.5 px-4">
                                {["Programado", "Pendiente"].includes(
                                  s.estado,
                                ) && (
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 cursor-pointer"
                                    checked={selectedIds.has(s._id)}
                                    onChange={() => toggleSelect(s._id)}
                                  />
                                )}
                              </td>
                            )}
                          <td className="py-2.5 px-4">
                            <p className="text-gray-700 font-medium text-xs">
                              {s.equipo?.cliente?.name || "—"}
                            </p>
                            {s.equipo?.cliente?.nit && (
                              <p className="text-xs text-gray-400 font-mono">
                                {s.equipo.cliente.nit}
                              </p>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-xs text-gray-600">
                            {s.equipo?.sede?.name || "—"}
                          </td>
                          <td className="py-2.5 px-4">
                            <button
                              className="text-blue-600 hover:underline text-left font-medium text-xs"
                              onClick={() =>
                                navigate(`/equipos/${s.equipo?._id}`)
                              }
                            >
                              {s.equipo?.marca?.name || "—"}{" "}
                              {s.equipo?.modelo?.name || ""}
                            </button>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">
                              {s.equipo?.serial || "—"}
                            </p>
                          </td>
                          <td className="py-2.5 px-4 text-xs text-gray-600">
                            {s.equipo?.area?.grupo?.name ? (
                              <span>{s.equipo.area.grupo.name} · </span>
                            ) : null}
                            {s.equipo?.area?.name || "—"}
                          </td>
                          <td className="py-2.5 px-4">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${TIPO_BADGE[s.tipo] || "bg-gray-100 text-gray-600 border border-gray-200"}`}
                            >
                              {s.tipo || "—"}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-xs text-gray-600">
                            {new Date(s.fechaProgramada).toLocaleDateString(
                              "es-CO",
                            )}
                          </td>
                          <td className="py-2.5 px-4">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ESTADO_BADGE[s.estado] || "bg-gray-100 text-gray-600 border border-gray-200"}`}
                            >
                              {s.estado}
                            </span>
                            {s.vencido && (
                              <span className="ml-1.5 text-xs font-semibold text-red-500">
                                VENCIDO
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-xs text-gray-600">
                            {s.personaAsignada ? (
                              s.personaAsignada.name
                            ) : (
                              <span className="text-gray-400 italic">
                                Sin asignar
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            {s.estado === "Programado" ? (
                              <div className="flex items-center justify-end gap-1.5">
                                {hasPermission("servicios.editar") && (
                                  <button
                                    onClick={() => handleEdit(s)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                                  >
                                    <Pencil className="h-3 w-3" /> Editar
                                  </button>
                                )}
                                {hasPermission("servicios.eliminar") && (
                                  <button
                                    onClick={() => handleDelete(s._id)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-red-50 border border-red-200 rounded-lg text-red-600 hover:bg-red-100 transition-colors"
                                  >
                                    <Trash2 className="h-3 w-3" /> Eliminar
                                  </button>
                                )}
                              </div>
                            ) : s.estado === "Completado" ? (
                              hasPermission("servicios.ver") ? (
                                <button
                                  onClick={() =>
                                    navigate(`/manage/${s._id}`, {
                                      state: { servicio: s },
                                    })
                                  }
                                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                  <Eye className="h-3 w-3" /> Ver
                                </button>
                              ) : null
                            ) : s.estado === "Borrador" ? (
                              // Solo el responsable la retoma, desde Mis servicios
                              <span
                                className="text-gray-400 italic text-xs"
                                title="Solo el responsable asignado puede retomarla desde Mis servicios"
                              >
                                En curso por el responsable
                              </span>
                            ) : s.estado === "Cerrado" ? (
                              hasPermission("servicios.ver") ? (
                                <button
                                  onClick={() => handleVerReporte(s._id)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-green-50 border border-green-200 rounded-lg text-green-700 hover:bg-green-100 transition-colors"
                                >
                                  <Eye className="h-3 w-3" />
                                  {isLoading === s._id
                                    ? "Cargando..."
                                    : "Ver acta"}
                                </button>
                              ) : null
                            ) : (
                              <span className="text-gray-400 italic text-xs">
                                No editable
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between text-sm text-gray-600 mb-6">
              <span className="text-xs text-gray-400">
                {serviciosVisibles.length === 0
                  ? "0"
                  : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, serviciosVisibles.length)}`}{" "}
                de {serviciosVisibles.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Anterior
                </button>
                <span className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-medium">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  Siguiente <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Tabla Alarmas */}
        {activeTab === "alarmas" && (
          <>
            {loadingAlarmas ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <LoaderPinwheel className="h-6 w-6 animate-spin text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-blue-700">
                  Analizando estado de equipos...
                </p>
              </div>
            ) : alarmas.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CalendarCheck2 className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-gray-700 font-semibold">
                  Sin alarmas activas
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Todos los equipos están al día con su mantenimiento.
                </p>
              </div>
            ) : (
              <>
                {/* Búsqueda alarmas */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
                  <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      value={alarmaSearch}
                      onChange={(e) => setAlarmaSearch(e.target.value)}
                      placeholder="Buscar por serial, marca, cliente…"
                      className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    />
                    {alarmaSearch && (
                      <button
                        onClick={() => setAlarmaSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
                  <div className="flex items-center justify-end px-4 py-2 border-b border-gray-100">
                    <PageSizeSelect
                      value={pageSize}
                      onChange={(size) => {
                        setPageSize(size);
                        setAlarmaPage(1);
                      }}
                    />
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                          <th className="py-3 px-4 text-left">Equipo</th>
                          <th className="py-3 px-4 text-left">
                            Cliente / Área
                          </th>
                          <th className="py-3 px-4 text-left">Tipo</th>
                          <th className="py-3 px-4 text-left">Estado</th>
                          <th className="py-3 px-4 text-left">
                            Fecha programada
                          </th>
                          <th className="py-3 px-4 text-left">Último mant.</th>
                          <th className="py-3 px-4 text-left">Periodicidad</th>
                          <th className="py-3 px-4 text-left">Responsable</th>
                          <th className="py-3 px-4 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {alarmasFiltradas.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="py-12 text-center">
                              <p className="text-gray-400 text-sm">
                                No se encontraron resultados
                              </p>
                            </td>
                          </tr>
                        ) : (
                          alarmasPaged.map((a, idx) => {
                            const eq = a.equipo;
                            const badgeClass =
                              a.estado === "Vencido"
                                ? "bg-red-50 text-red-700 border border-red-200"
                                : a.estado === "Fuera de periodicidad"
                                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                                  : "bg-amber-50 text-amber-700 border border-amber-200";
                            return (
                              <tr
                                key={idx}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="py-2.5 px-4">
                                  <button
                                    className="text-blue-600 hover:underline text-left font-medium text-xs"
                                    onClick={() =>
                                      navigate(`/equipos/${eq._id}`)
                                    }
                                  >
                                    {eq.marca?.name} {eq.modelo?.name}
                                  </button>
                                  <p className="text-xs text-gray-400 font-mono mt-0.5">
                                    {eq.serial || "—"}
                                  </p>
                                </td>
                                <td className="py-2.5 px-4 text-xs text-gray-600">
                                  <p className="font-medium text-gray-700">
                                    {eq.cliente?.name || "—"}
                                  </p>
                                  <p className="text-gray-400">
                                    {eq.area?.grupo?.name
                                      ? `${eq.area.grupo.name} · `
                                      : ""}
                                    {eq.area?.name || "—"}
                                  </p>
                                </td>
                                <td className="py-2.5 px-4">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${TIPO_BADGE[a.cronograma?.tipo] || "bg-gray-100 text-gray-600 border border-gray-200"}`}
                                  >
                                    {a.cronograma?.tipo || "Preventivo"}
                                  </span>
                                </td>
                                <td className="py-2.5 px-4">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClass}`}
                                  >
                                    {a.estado}
                                  </span>
                                </td>
                                <td className="py-2.5 px-4 text-xs text-gray-600">
                                  {a.cronograma?.fechaProgramada
                                    ? new Date(
                                        a.cronograma.fechaProgramada,
                                      ).toLocaleDateString("es-CO")
                                    : "—"}
                                </td>
                                <td className="py-2.5 px-4 text-xs text-gray-600">
                                  {eq.ultimaMantencion
                                    ? new Date(
                                        eq.ultimaMantencion,
                                      ).toLocaleDateString("es-CO")
                                    : "—"}
                                </td>
                                <td className="py-2.5 px-4 text-xs text-gray-600">
                                  {eq.periocidadMantenimiento
                                    ? `${eq.periocidadMantenimiento} días`
                                    : "—"}
                                </td>
                                <td className="py-2.5 px-4 text-xs text-gray-600">
                                  {a.cronograma?.personaAsignada?.name || "—"}
                                </td>
                                <td className="py-2.5 px-4 text-right">
                                  {hasPermission("servicios.crear") && (
                                    <button
                                      onClick={() => setAlarmaModal(a)}
                                      className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                        a.estado === "No agendado"
                                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                                          : "bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"
                                      }`}
                                    >
                                      {a.estado === "No agendado"
                                        ? "Programar"
                                        : "Editar"}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Paginación alarmas */}
                <div className="flex items-center justify-between text-sm text-gray-600 mb-6">
                  <span className="text-xs text-gray-400">
                    {alarmasFiltradas.length === 0
                      ? "0"
                      : `${(alarmaPage - 1) * pageSize + 1}–${Math.min(alarmaPage * pageSize, alarmasFiltradas.length)}`}{" "}
                    de {alarmasFiltradas.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAlarmaPage((p) => Math.max(1, p - 1))}
                      disabled={alarmaPage === 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" /> Anterior
                    </button>
                    <span className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-medium">
                      {alarmaPage} / {alarmasTotalPages}
                    </span>
                    <button
                      onClick={() =>
                        setAlarmaPage((p) => Math.min(alarmasTotalPages, p + 1))
                      }
                      disabled={alarmaPage === alarmasTotalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    >
                      Siguiente <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {showForm && (
        <CronogramaForm
          mantenimiento={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}

      {showFormMasive && (
        <CronogramaMasivoForm
          mantenimiento={editingMasive}
          onClose={() => {
            setShowFormMasive(false);
            setEditingMasive(null);
          }}
          onSave={fetchServicios}
        />
      )}

      {showAsignar && (
        <AsignarForm
          onClose={() => setShowAsignar(false)}
          onSave={fetchServicios}
        />
      )}

      {showSegundoFirmante && (
        <AsignarSegundoFirmanteModal
          onClose={() => setShowSegundoFirmante(false)}
          onAsignado={fetchServicios}
        />
      )}

      {alarmaModal && (
        <AlarmaModal
          alarma={alarmaModal}
          onClose={() => setAlarmaModal(null)}
          onSave={fetchAlarmas}
        />
      )}
    </div>
  );
};

export default ServiciosPage;
